import { reactive, computed, inject } from 'vue';
import { MoneyUtils } from '../utils/MoneyUtils';
/**
 * Enhanced Composable for Special Deals Management
 * Shared across all restaurant projects
 * Handles promotional offers, discounts, and featured items
 */
export function useSpecialDeals() {
    const apiClient = inject('apiClient');
    if (!apiClient) {
        throw new Error('RestaurantApiClient not provided. Make sure to provide it in your app.');
    }
    const state = reactive({
        deals: [],
        featuredDeals: [],
        loading: false,
        error: null,
        lastUpdated: null
    });
    /**
     * Load special deals from API
     */
    const loadSpecialDeals = async (limit) => {
        state.loading = true;
        state.error = null;
        try {
            console.log('🔥 Loading special deals...');
            const response = await apiClient.getSpecialDeals(limit || 12);
            if (response.status === 'success' && response.data) {
                // Transform MenuItem[] to SpecialDeal[]
                const deals = response.data.map(item => ({
                    ...item,
                    dealType: 'percentage', // Default deal type
                    discountPercentage: calculateDiscountPercentage(item.originalPrice, item.price),
                    discountAmount: (item.originalPrice || item.price) - item.price,
                    badgeText: getBadgeText(item),
                    isLimitedTime: true
                }));
                state.deals = deals;
                state.lastUpdated = new Date();
                console.log(`✅ Special deals loaded: ${deals.length} deals`);
                return deals;
            }
            else {
                state.error = response.message || 'Failed to load special deals';
                console.error('❌ Failed to load special deals:', state.error);
                return [];
            }
        }
        catch (error) {
            state.error = error.message || 'Unknown error occurred';
            console.error('❌ Special deals loading error:', error);
            return [];
        }
        finally {
            state.loading = false;
        }
    };
    /**
     * Load featured deals (top recommendations)
     */
    const loadFeaturedDeals = async (limit = 6) => {
        try {
            console.log('⭐ Loading featured deals...');
            const allDeals = await loadSpecialDeals();
            // Filter and sort for featured deals
            const featured = allDeals
                .filter(deal => deal.popular || (deal.discountPercentage && deal.discountPercentage >= 15))
                .sort((a, b) => (b.discountPercentage || 0) - (a.discountPercentage || 0))
                .slice(0, limit);
            state.featuredDeals = featured;
            console.log(`✅ Featured deals loaded: ${featured.length} deals`);
            return featured;
        }
        catch (error) {
            console.error('❌ Featured deals loading error:', error);
            return [];
        }
    };
    /**
     * Get deals by category
     */
    const getDealsByCategory = (category) => {
        return state.deals.filter(deal => deal.category === category);
    };
    /**
     * Get active deals (not expired)
     */
    const getActiveDeals = () => {
        const now = new Date();
        return state.deals.filter(deal => {
            if (!deal.validUntil)
                return true;
            return new Date(deal.validUntil) > now;
        });
    };
    /**
     * Calculate discount percentage
     */
    const calculateDiscountPercentage = (originalPrice, currentPrice) => {
        if (!originalPrice || !currentPrice || originalPrice <= currentPrice)
            return 0;
        return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
    };
    const calculateDiscount = (deal) => {
        if (typeof deal.discountPercentage === 'number') {
            return deal.discountPercentage;
        }
        const originalCent = deal.originalPriceCent ?? (typeof deal.originalPrice === 'number' ? MoneyUtils.euroToCent(deal.originalPrice) : undefined);
        const currentCent = deal.priceCent ?? (typeof deal.price === 'number' ? MoneyUtils.euroToCent(deal.price) : undefined);
        if (typeof originalCent !== 'number' || typeof currentCent !== 'number') {
            return 0;
        }
        return MoneyUtils.calculateDiscountPercentage(originalCent, currentCent);
    };
    /**
     * Get badge text for deal
     */
    const getBadgeText = (item) => {
        if (item.popular)
            return 'Beliebt';
        if (item.originalPrice && item.originalPrice > item.price) {
            const discount = calculateDiscountPercentage(item.originalPrice, item.price);
            return discount > 0 ? `-${discount}%` : 'Angebot';
        }
        return 'Neu';
    };
    /**
     * Check if item is on special offer
     */
    const isSpecialOffer = (item) => {
        return !!(item.originalPrice && item.originalPrice > item.price);
    };
    /**
     * Format discount text
     */
    const formatDiscount = (deal) => {
        if (deal.discountPercentage && deal.discountPercentage > 0) {
            return `${deal.discountPercentage}% sparen`;
        }
        if (deal.discountAmount && deal.discountAmount > 0) {
            return `€${deal.discountAmount.toFixed(2)} sparen`;
        }
        return 'Sonderangebot';
    };
    /**
     * Get time remaining for limited offers
     */
    const getTimeRemaining = (deal) => {
        if (!deal.isLimitedTime || !deal.validUntil)
            return null;
        const now = new Date();
        const end = new Date(deal.validUntil);
        const diff = end.getTime() - now.getTime();
        if (diff <= 0)
            return 'Abgelaufen';
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        if (hours > 24) {
            const days = Math.floor(hours / 24);
            return `Noch ${days} Tag${days !== 1 ? 'e' : ''}`;
        }
        if (hours > 0) {
            return `Noch ${hours}h ${minutes}m`;
        }
        return `Noch ${minutes}m`;
    };
    // Computed Properties
    const hasDeals = computed(() => state.deals.length > 0);
    const hasFeaturedDeals = computed(() => state.featuredDeals.length > 0);
    const isLoading = computed(() => state.loading);
    const hasError = computed(() => !!state.error);
    const error = computed(() => state.error);
    const activeDealsCount = computed(() => getActiveDeals().length);
    return {
        // State
        deals: computed(() => state.deals),
        featuredDeals: computed(() => state.featuredDeals),
        loading: isLoading,
        error,
        lastUpdated: computed(() => state.lastUpdated),
        // Methods
        loadSpecialDeals,
        fetchSpecialDeals: loadSpecialDeals,
        loadFeaturedDeals,
        getDealsByCategory,
        getActiveDeals,
        isSpecialOffer,
        formatDiscount,
        getTimeRemaining,
        calculateDiscountPercentage,
        calculateDiscount,
        formatPrice: (price, priceCent) => {
            if (typeof priceCent === 'number' && MoneyUtils.isValidCentAmount(priceCent)) {
                return MoneyUtils.formatCurrency(priceCent);
            }
            if (typeof price === 'number' && Number.isFinite(price)) {
                return MoneyUtils.formatCurrency(MoneyUtils.euroToCent(price));
            }
            return MoneyUtils.formatCurrency(0);
        },
        convertToCartItem: (deal) => {
            const centValue = deal.priceCent ?? (typeof deal.price === 'number' ? MoneyUtils.euroToCent(deal.price) : 0);
            const originalCent = deal.originalPriceCent ?? (typeof deal.originalPrice === 'number' ? MoneyUtils.euroToCent(deal.originalPrice) : undefined);
            return {
                ...deal,
                id: deal.id || deal._id,
                _id: deal._id || deal.id,
                price: MoneyUtils.centToEuro(centValue),
                priceCent: centValue,
                originalPrice: typeof originalCent === 'number' ? MoneyUtils.centToEuro(originalCent) : deal.originalPrice,
                originalPriceCent: originalCent,
                quantity: 1,
                totalPrice: MoneyUtils.centToEuro(centValue),
                totalPriceCent: centValue,
                calculatedPriceCent: centValue
            };
        },
        // Utils
        hasDeals,
        hasFeaturedDeals,
        isLoading,
        hasError,
        activeDealsCount
    };
}
